import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Header from "@/components/Header";
import AdminTabsNav from "@/components/AdminTabsNav";
import { useWallet } from "@/context/WalletContext";
import {
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
  Wrench,
  Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminOrphans — cluster-wide orphan-agent dashboard.
 * Lists wallets with orphan counts (sorted desc) and offers per-wallet Heal +
 * a single Heal All button.
 */
export default function AdminOrphans() {
  const { getAuthHeaders, isConnected } = useWallet();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [healingAll, setHealingAll] = useState(false);
  const [healingAddr, setHealingAddr] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/orphans/scan?limit=200`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 403) {
        toast.error("Admin access required — connect an admin wallet");
        return;
      }
      const data = await res.json();
      if (data?.success) setScan(data);
    } catch (e) {
      toast.error(`Scan failed: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (isConnected) refresh();
  }, [isConnected, refresh]);

  const healOne = async (stxAddress) => {
    setHealingAddr(stxAddress);
    try {
      const res = await fetch(
        `${API}/api/admin/users/${stxAddress}/heal-orphan-agents`,
        { method: "POST", headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (data?.success) {
        toast.success(`Reassigned ${data.agents_reassigned}`);
        await refresh();
      } else {
        toast.error("Heal failed");
      }
    } catch (e) {
      toast.error(`Heal failed: ${e?.message || e}`);
    } finally {
      setHealingAddr(null);
    }
  };

  const healAll = async () => {
    setHealingAll(true);
    try {
      const res = await fetch(`${API}/api/admin/orphans/heal-all`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data?.success) {
        toast.success(
          `Healed ${data.total_reassigned} agents across ${data.wallets_processed} wallets`
        );
        await refresh();
      } else {
        toast.error("Heal-all failed");
      }
    } catch (e) {
      toast.error(`Heal-all failed: ${e?.message || e}`);
    } finally {
      setHealingAll(false);
    }
  };

  const wallets = scan?.wallets || [];
  const totalOrphans = scan?.total_orphans || 0;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="p-4 md:p-6">
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="font-heading text-xl font-bold flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-orange-400" />
                Orphan-Agent Recovery
              </h1>
              <p className="text-xs text-muted-foreground">
                Cluster-wide scan of agents whose user_id is stale (e.g., post-migration).
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={loading}
              data-testid="admin-orphans-refresh"
            >
              <RefreshCw
                className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`}
              />
              Rescan
            </Button>
          </div>

          {/* Shared admin tab nav — placed AFTER the title to match the
              layout of /admin/withdrawals (header first, tabs underneath). */}
          <AdminTabsNav active="orphans" />

          {/* Summary */}
          <Card
            className={`border ${
              totalOrphans > 0
                ? "border-orange-500/40 bg-orange-500/5"
                : "border-green-500/30 bg-green-500/5"
            }`}
            data-testid="admin-orphans-summary"
          >
            <CardContent className="p-4 flex items-center gap-3">
              {totalOrphans > 0 ? (
                <ShieldAlert className="w-6 h-6 text-orange-400" />
              ) : (
                <ShieldCheck className="w-6 h-6 text-green-400" />
              )}
              <div className="flex-1">
                <p
                  className={`font-mono text-2xl font-bold ${
                    totalOrphans > 0 ? "text-orange-300" : "text-green-400"
                  }`}
                  data-testid="admin-orphans-total"
                >
                  {totalOrphans.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">
                  orphans across{" "}
                  <span data-testid="admin-orphans-wallet-count">
                    {wallets.length}
                  </span>{" "}
                  wallet{wallets.length !== 1 ? "s" : ""}
                </p>
              </div>
              {totalOrphans > 0 && (
                <Button
                  onClick={healAll}
                  disabled={healingAll}
                  className="bg-orange-500 hover:bg-orange-600 text-black"
                  data-testid="admin-orphans-heal-all"
                >
                  {healingAll ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Healing all…
                    </>
                  ) : (
                    <>
                      <Wrench className="w-3 h-3 mr-1" />
                      Heal all {totalOrphans}
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Per-wallet leaderboard */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm text-muted-foreground">
                Wallets sorted by orphan count
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading && wallets.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Scanning…
                </div>
              ) : wallets.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  No orphan agents detected. All wallets healthy.
                </div>
              ) : (
                <div className="space-y-2">
                  {wallets.map((w) => (
                    <div
                      key={w.stx_address}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border/40 bg-muted/20"
                      data-testid={`admin-orphan-row-${w.stx_address}`}
                    >
                      <div className="flex-1 min-w-0">
                        <code className="text-xs font-mono text-muted-foreground truncate block">
                          {w.stx_address.slice(0, 12)}…{w.stx_address.slice(-6)}
                        </code>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          current uid:{" "}
                          <code className="font-mono">{w.current_user_id?.slice(0, 14)}…</code>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className="bg-orange-500/15 text-orange-300 border-orange-500/30 font-mono"
                      >
                        {w.orphan_count} orphan{w.orphan_count !== 1 ? "s" : ""}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => healOne(w.stx_address)}
                        disabled={healingAddr === w.stx_address}
                        data-testid={`admin-orphan-heal-${w.stx_address}`}
                      >
                        {healingAddr === w.stx_address ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Wrench className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
